/**
 * Employee Profile Hooks
 *
 * React Query hooks for managing reusable employee profiles.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  employeeProfilesApi,
  ListProfilesParams,
} from '@/services/api/employeeProfilesApi';
import {
  CreateEmployeeProfileRequest,
  UpdateEmployeeProfileRequest,
} from '@/types/nib/employeeProfile';

// Query keys
export const profileKeys = {
  all: ['employee-profiles'] as const,
  lists: () => [...profileKeys.all, 'list'] as const,
  list: (params?: ListProfilesParams) =>
    [...profileKeys.lists(), params ?? {}] as const,
};

// =============================================================================
// QUERIES
// =============================================================================

/**
 * Fetch profiles with server-side search, category filter, sorting, pagination.
 * Accepts either a string (legacy) or full ListProfilesParams.
 */
export function useProfileList(params?: string | ListProfilesParams) {
  const resolved: ListProfilesParams =
    typeof params === 'string' ? { search: params } : (params ?? {});

  return useQuery({
    queryKey: profileKeys.list(resolved),
    queryFn: () => employeeProfilesApi.listProfiles(resolved),
    select: (data) => data.profiles,
  });
}

/**
 * Fetch profiles with full pagination metadata (total, page, per_page).
 */
export function useProfileListPaginated(params: ListProfilesParams = {}) {
  return useQuery({
    queryKey: profileKeys.list(params),
    queryFn: () => employeeProfilesApi.listProfiles(params),
  });
}

// =============================================================================
// MUTATIONS
// =============================================================================

export function useCreateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateEmployeeProfileRequest) =>
      employeeProfilesApi.createProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileKeys.lists() });
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      profileId,
      data,
    }: {
      profileId: string;
      data: UpdateEmployeeProfileRequest;
    }) => employeeProfilesApi.updateProfile(profileId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileKeys.lists() });
    },
  });
}

export function useDeleteProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (profileId: string) =>
      employeeProfilesApi.deleteProfile(profileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileKeys.lists() });
    },
  });
}

export function useImportProfiles() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (submissionId: string) =>
      employeeProfilesApi.importFromSubmission(submissionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileKeys.lists() });
    },
  });
}
